// Spam filter module for Ferienwohnungen Lindenheim contact form
// Pure functions for spam detection, routing decisions, and quota-cap logic.
// Importable as ES module (tests) and via window.* (browser inline scripts).

export const SERVICE_ID = 'service_zrg82nb';
export const LEGIT_TEMPLATE_ID = 'template_tm487039kdlikrh';
export const SPAM_TEMPLATE_ID = 'template_2ibqb7o'; // Fewolindenheim Spam template
export const SPAM_AUDIT_LS_KEY = 'lh:spam-audit:lastSentDate';

// REVISED 2026-05-25: High-precision 2-rule variant.
// Only two strong-signal rules; optimized for zero false-positives on real
// guest name patterns (couples, surname-first, digits, international chars).
export function isGibberishName(name) {
    const trimmed = name.trim();
    if (trimmed.length === 0) return false; // empty handled by SPAM-03 (message check, not name)

    // Rule 1: Single token (no whitespace) of length >= 8 mixing letters AND digits.
    // FLAGS: "Dg54asdkfoda+-", "abc123def456"
    // PASSES: "Henry 8th" (has space), "+49 30 12345" (has space), "iPhone" (6 chars)
    for (const token of trimmed.split(/\s+/)) {
        if (token.length >= 8 && /\p{L}/u.test(token) && /\d/.test(token)) {
            return true;
        }
    }

    // Rule 2: Any letters-only "word" (split on whitespace/hyphen/apostrophe/period)
    // of length >= 6 with ZERO vowels. Broad Unicode vowel set incl. y, w (Welsh).
    // FLAGS: "asdfghjkl" (9 consonants), "bcdfghjk" (8 consonants)
    // PASSES: "Müller" (has ü), "Brno" (4 letters), "Llwyd" (has w + y)
    const VOWELS = /[aeiouywäöüáéíóúàèìòùâêîôûãñõåøæœÿ]/i;
    for (const word of trimmed.split(/[\s\-'.]+/u)) {
        if (word.length >= 6 && /^\p{L}+$/u.test(word) && !VOWELS.test(word)) {
            return true;
        }
    }

    return false;
}

export function isSpammyMessage(message) {
    return message.trim() === '';
}

export function chooseTemplate({ honeypot, name, message }) {
    const spamReasons = [];

    if (honeypot.trim().length > 0) {
        spamReasons.push('honeypot_filled');
    }

    if (isSpammyMessage(message)) {
        spamReasons.push('empty_message');
    }

    if (isGibberishName(name)) {
        spamReasons.push('gibberish_name');
    }

    if (spamReasons.length > 0) {
        return { templateId: SPAM_TEMPLATE_ID, spamReason: spamReasons.join(', ') };
    }

    return { templateId: LEGIT_TEMPLATE_ID, spamReason: null };
}

export function shouldSendSpamAudit({ today, lastSentDate }) {
    return Boolean(!lastSentDate || lastSentDate !== today);
}

export function markSpamAuditSent({ today, storage = localStorage }) {
    storage.setItem(SPAM_AUDIT_LS_KEY, today);
}

// Bring to global scope for browser inline scripts (same pattern as translate.js)
if (typeof window !== 'undefined') {
    window.isGibberishName = isGibberishName;
    window.isSpammyMessage = isSpammyMessage;
    window.chooseTemplate = chooseTemplate;
    window.shouldSendSpamAudit = shouldSendSpamAudit;
    window.markSpamAuditSent = markSpamAuditSent;
    window.SPAM_AUDIT_LS_KEY = SPAM_AUDIT_LS_KEY;
}
